import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';

interface CouponDetailsProps {
  coupon: number | null;
  couponFrequency: string | null;
  couponMinPct: number | null;
  couponMaxPct: number | null;
  couponType: string | null;
  referenceRate: string | null;
  sectionTitle?: string;
  // Props to control which fields to show
  showCouponMinPct?: boolean;
  showCouponMaxPct?: boolean;
  showReferenceRate?: boolean;
  couponDetailsProps?: Record<string, boolean>;
}

export const CouponDetails: React.FC<CouponDetailsProps> = ({
  coupon,
  couponFrequency,
  couponMinPct,
  couponMaxPct,
  couponType,
  referenceRate,
  sectionTitle,
  showCouponMinPct = true,
  showCouponMaxPct = true,
  showReferenceRate = true,
  couponDetailsProps = {},
}) => {
  const formatPct = (value: number | null, decimals = 2) => {
    if (value === null || value === 0) return 'None';
    return `${value.toFixed(decimals)}%`;
  };

  // Helper function to check if a prop should be shown
  const shouldShow = (propName: string, defaultValue: boolean = true): boolean => {
    if (couponDetailsProps[propName] !== undefined) {
      return couponDetailsProps[propName];
    }
    return defaultValue;
  };

  // Use props or couponDetailsProps to determine visibility
  const displayCouponMinPct = shouldShow('couponMinPct', showCouponMinPct);
  const displayCouponMaxPct = shouldShow('couponMaxPct', showCouponMaxPct);
  const displayReferenceRate = shouldShow('referenceRate', showReferenceRate);

  return (
    <View style={[styles.container, Platform.OS === 'web' && { padding: 16, marginBottom: 12 }]}>
      <Text style={styles.sectionTitle}>{sectionTitle || 'Coupon details'}</Text>
      <View style={styles.separator} />

      <View style={styles.grid}>
        <View style={styles.row}>
          <View style={styles.item}>
            <Text style={styles.label}>Coupon</Text>
            <Text style={styles.value}>
              {coupon !== null ? `${coupon.toFixed(2)}%` : '-'}
            </Text>
          </View>

          <View style={styles.item}>
            <Text style={styles.label}>Coupon frequency</Text>
            <Text style={styles.value}>{couponFrequency || '-'}</Text>
          </View>
        </View>

        {(displayCouponMinPct || displayCouponMaxPct) && (
        <View style={styles.row}>
          {displayCouponMinPct && (
          <View style={styles.item}>
            <Text style={styles.label}>Coupon Min %</Text>
            <Text style={styles.value}>{formatPct(couponMinPct)}</Text>
          </View>
          )}

          {displayCouponMaxPct && (
          <View style={styles.item}>
            <Text style={styles.label}>Coupon Max %</Text>
            <Text style={styles.value}>{formatPct(couponMaxPct)}</Text>
          </View>
          )}
        </View>
        )}

        <View style={styles.row}>
          <View style={styles.item}>
            <Text style={styles.label}>Coupon type</Text>
            <Text style={styles.value}>{couponType || 'None'}</Text>
          </View>

          {displayReferenceRate && (
          <View style={styles.item}>
            <Text style={styles.label}>Reference rate</Text>
            <Text style={styles.value}>{referenceRate || 'None'}</Text>
          </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  separator: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginBottom: 16,
  },
  grid: {
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
  },
  item: {
    flex: 1,
  },
  label: {
    fontSize: 15,
    color: '#757575',
    marginBottom: 4,
  },
  value: {
    fontSize: 14,
    fontWeight: '600',
    color: '#212121',
  },
});
